import { afterNextRender, ChangeDetectionStrategy, Component, DestroyRef, inject, signal, viewChild, WritableSignal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { I18NextPipe } from 'angular-i18next';
import { ButtonComponent, ModalComponent, ModalFooterDirective } from 'mbs-ui-kit';
import { PlanTypes } from 'mbs-ui-shared@models/PlanTypes.enum';
import { WhatBackupStepValue } from 'wizards/models/what-backup-models';
import { WhatBackupTreeStepValue } from 'wizards/models/what-backup-tree-model';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import { WhatBackupStepComponent } from 'wizards/steps/what-backup-step/what-backup-step.component';
import { WhatBackupTreeStepComponent } from 'wizards/steps/what-backup-tree-step/what-backup-tree-step.component';
import { CompanyBackupTemplate } from '../../models/company-wizard-models';
import { borrowPlanContext, planContextFromTemplate } from '../../utils/company-wizard-plan-context.util';

export type WhatBackupModalData = {
  template: CompanyBackupTemplate;
};

export type WhatBackupModalResult = {
  whatBackupStep?: WhatBackupStepValue;
  whatBackupTreeStep?: WhatBackupTreeStepValue;
};

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonComponent,
    FormsModule,
    I18NextPipe,
    ModalComponent,
    ModalFooterDirective,
    WhatBackupStepComponent,
    WhatBackupTreeStepComponent
  ],
  selector: 'mbs-what-backup-modal',
  standalone: true,
  templateUrl: './what-backup-modal.component.html'
})
export class WhatBackupModalComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly mainService = inject(RemoteManagementWizardsService);

  protected readonly baseModal = viewChild(ModalComponent);
  protected readonly whatBackupStepComponent = viewChild(WhatBackupStepComponent);
  protected readonly whatBackupTreeStepComponent = viewChild(WhatBackupTreeStepComponent);

  public readonly isImageBased: WritableSignal<boolean> = signal(false);
  public readonly ready: WritableSignal<boolean> = signal(false);
  public readonly whatBackupStep: WritableSignal<WhatBackupStepValue> = signal(null);
  public readonly whatBackupTreeStep: WritableSignal<WhatBackupTreeStepValue> = signal(null);

  constructor() {
    // ModalService assigns data only after open() returns, and a signal viewChild is unresolved until the view is checked
    afterNextRender(() => this.applyModalData());
  }

  public saveClickHandler(): void {
    this.whatBackupStepComponent()?.forceValid();
    this.whatBackupTreeStepComponent()?.forceValid();

    if (this.whatBackupTreeStepComponent()?.value?.valid === false) return;

    this.baseModal().save({
      whatBackupStep: this.whatBackupStep(),
      whatBackupTreeStep: this.whatBackupTreeStep()
    } as WhatBackupModalResult);
  }

  public cancelClickHandler(): void {
    this.baseModal().close();
  }

  // Private section
  private applyModalData(): void {
    const data = this.baseModal()?.data as WhatBackupModalData;

    if (!data) return;

    const isImageBased = data.template.planType === PlanTypes.BackupDiskImagePlan;

    // Both steps read the service in their constructors, so the context has to be in place before they render
    this.destroyRef.onDestroy(borrowPlanContext(this.mainService, planContextFromTemplate(data.template)));

    this.isImageBased.set(isImageBased);
    this.whatBackupStep.set(data.template.whatBackupStep);
    this.whatBackupTreeStep.set(data.template.whatBackupTreeStep);
    this.ready.set(true);
  }
}
